/**
 * BarcodeScannerModal - Extracted from App.js Phase 4
 * Product barcode scanner with camera, manual entry, and Nutrition-Label-Photo fallback
 * for glossy / curved / unscannable packaging.
 */
import React, { useRef, useState } from 'react';
import { NutritionDataDisclaimer } from '../ui/UtilityComponents';

const BarcodeScannerModal = ({
  isOpen,
  onClose,
  analyzing,
  scannerActive,
  startBarcodeScanner,
  stopBarcodeScanner,
  barcodeInput,
  setBarcodeInput,
  scanBarcode,
  barcodeResult,
  setBarcodeResult,
  barcodeServings,
  setBarcodeServings,
  addBarcodeProductToMeals,
  scanNutritionLabel,
}) => {
  const labelFileInputRef = useRef(null);
  const [labelProductName, setLabelProductName] = useState('');
  const [labelMode, setLabelMode] = useState(false);
  const [saveAsFavorite, setSaveAsFavorite] = useState(true);

  if (!isOpen) return null;

  const handleLabelFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !scanNutritionLabel) return;
    scanNutritionLabel(file, labelProductName);
    e.target.value = '';
  };

  return (
    <div className="modal-overlay" onClick={async () => { await stopBarcodeScanner(); onClose(); }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2 className="heading-3">Scan Product</h2>
        <p className="modal-description">Scan barcode, or photograph the nutrition panel</p>
        <div className="premium-badge">Premium Feature</div>

        <div id="barcode-reader" style={{ width: '100%', marginBottom: '16px', display: scannerActive ? 'block' : 'none' }}></div>

        {!scannerActive && !barcodeResult && !labelMode && (
          <>
            <button
              className="btn-primary full-width"
              data-testid="start-barcode-scanner-btn"
              onClick={startBarcodeScanner}
              style={{ marginBottom: '12px' }}
            >
              📷 Start Camera Scanner
            </button>

            <button
              className="btn-secondary full-width"
              data-testid="open-label-photo-btn"
              onClick={() => setLabelMode(true)}
              style={{ marginBottom: '16px' }}
            >
              📋 Photograph Nutrition Label Instead
            </button>
            <p style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'center', marginTop: '-8px', marginBottom: '16px' }}>
              Works for glossy, curved, or damaged packaging
            </p>

            <p style={{ textAlign: 'center', margin: '16px 0', color: 'var(--text-secondary)' }}>OR</p>
            <div className="search-input-group">
              <input type="text" className="search-input" placeholder="Enter barcode number (e.g., 012000161551)"
                data-testid="barcode-manual-input"
                value={barcodeInput} onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && scanBarcode()} />
              <button className="btn-secondary" data-testid="barcode-lookup-btn" onClick={scanBarcode} disabled={analyzing}>
                {analyzing ? 'Looking up...' : 'Lookup'}
              </button>
            </div>
            {barcodeInput && !analyzing && (
              <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Detected: <strong style={{ color: '#667eea' }}>{barcodeInput}</strong> — tap Lookup to confirm, or re-scan
              </p>
            )}
          </>
        )}

        {labelMode && !barcodeResult && !scannerActive && (
          <div data-testid="label-photo-mode" style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px', lineHeight: 1.5 }}>
              Photograph the <strong>Nutrition Facts</strong> panel directly. Make sure the panel fills the frame and is well-lit.
            </p>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>
              What is this product? <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              className="search-input"
              data-testid="label-product-name-input"
              value={labelProductName}
              onChange={(e) => setLabelProductName(e.target.value)}
              placeholder="e.g., Organic Greek Yogurt"
              style={{ width: '100%', marginBottom: '14px' }}
            />
            <input
              ref={labelFileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="label-photo-file-input"
              onChange={handleLabelFileSelected}
              style={{ display: 'none' }}
            />
            <button
              className="btn-primary full-width"
              data-testid="capture-label-photo-btn"
              onClick={() => labelFileInputRef.current?.click()}
              disabled={analyzing}
              style={{ marginBottom: '8px' }}
            >
              {analyzing ? 'Reading label…' : '📸 Take / Choose Photo'}
            </button>
            <button
              className="btn-secondary full-width"
              data-testid="back-to-scanner-btn"
              onClick={() => { setLabelMode(false); setLabelProductName(''); }}
              disabled={analyzing}
            >
              Back
            </button>
          </div>
        )}

        {scannerActive && (
          <>
            <p style={{ textAlign: 'center', color: 'var(--brand-primary)', marginBottom: '16px' }}>Point camera at barcode...</p>
            <button className="btn-secondary full-width" onClick={stopBarcodeScanner}>Stop Scanner</button>
          </>
        )}

        {barcodeResult && (
          <div className="search-results barcode-result">
            <h3 className="result-title">{barcodeResult.food_items}</h3>
            {barcodeResult.brand && <p className="product-brand">{barcodeResult.brand}</p>}
            {barcodeResult.source === 'nutrition_label_photo' && (
              <p data-testid="label-photo-badge" style={{
                fontSize: '11px', color: '#10b981', fontWeight: '600',
                background: 'rgba(16, 185, 129, 0.12)',
                padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginBottom: '8px'
              }}>
                Read from label photo {barcodeResult.confidence ? `· ${barcodeResult.confidence} confidence` : ''}
              </p>
            )}
            {barcodeResult.ai_estimated && barcodeResult.source !== 'nutrition_label_photo' && (
              <p data-testid="barcode-ai-badge" style={{
                fontSize: '11px', color: '#667eea', fontWeight: '600',
                background: 'rgba(102, 126, 234, 0.1)',
                padding: '4px 8px', borderRadius: '4px', display: 'inline-block', marginBottom: '8px'
              }}>
                AI Estimated Nutrition
              </p>
            )}
            <div className="result-nutrition">
              <p><strong>Serving Size:</strong> {barcodeResult.serving_size || 'N/A'}</p>
              <p style={{ marginTop: '8px', marginBottom: '8px' }}><strong>Per Serving:</strong></p>
              <p><strong>Calories:</strong> {Math.round(barcodeResult.calories * barcodeServings)} kcal</p>
              <p><strong>Protein:</strong> {(barcodeResult.protein * barcodeServings).toFixed(1)}g</p>
              <p><strong>Carbs:</strong> {(barcodeResult.carbs * barcodeServings).toFixed(1)}g</p>
              <p><strong>Fat:</strong> {(barcodeResult.fat * barcodeServings).toFixed(1)}g</p>
              <p><strong>Fiber:</strong> {(barcodeResult.fiber * barcodeServings).toFixed(1)}g</p>
              <p><strong>Sugar:</strong> {(barcodeResult.sugar * barcodeServings).toFixed(1)}g</p>
              <p><strong>Sodium:</strong> {Math.round(barcodeResult.sodium * barcodeServings)}mg</p>
            </div>
            <NutritionDataDisclaimer />
            <div style={{ margin: '16px 0' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Number of Servings:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="btn-secondary" onClick={() => setBarcodeServings(Math.max(0.25, barcodeServings - 0.25))}
                  style={{ padding: '8px 12px', fontSize: '14px' }} title="Decrease by 0.25">-0.25</button>
                <input type="text" inputMode="decimal" value={barcodeServings}
                  onChange={(e) => setBarcodeServings(Math.max(0.25, parseFloat(e.target.value) || 1))}
                  step="0.25" min="0.25"
                  style={{ width: '80px', padding: '8px', textAlign: 'center', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px' }} />
                <button className="btn-secondary" onClick={() => setBarcodeServings(barcodeServings + 0.25)}
                  style={{ padding: '8px 12px', fontSize: '14px' }} title="Increase by 0.25">+0.25</button>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                Use 0.25 increments for small portions (e.g., splash of milk in coffee)
              </p>
            </div>

            {/* Save as Favorite toggle — pre-checked because users almost always
                re-encounter scanned products. Reuses the existing Favorites
                infrastructure for one-tap re-logging next time. */}
            <label
              data-testid="save-as-favorite-toggle"
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', marginBottom: '12px',
                background: 'rgba(102, 126, 234, 0.06)',
                border: '1px solid rgba(102, 126, 234, 0.18)',
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={saveAsFavorite}
                onChange={(e) => setSaveAsFavorite(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }}
              />
              <span style={{ lineHeight: 1.4 }}>
                <strong>Save as Favorite</strong> for one-tap re-logging next time
              </span>
            </label>

            <button className="btn-primary full-width" onClick={() => addBarcodeProductToMeals({ saveAsFavorite })}>
              Add {barcodeServings} Serving{barcodeServings !== 1 ? 's' : ''} to My Meals
            </button>
          </div>
        )}

        <button className="btn-secondary full-width" onClick={async () => {
          await stopBarcodeScanner();
          onClose();
          setBarcodeInput('');
          setBarcodeResult(null);
          setBarcodeServings(1);
          setLabelMode(false);
          setLabelProductName('');
          setSaveAsFavorite(true);
        }}>Close</button>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
